// "Import from Otter" (docs/specs/otter-import-1.md): connect an Otter account, start a
// history import, and watch its progress. Only reachable when the backend has the feature on
// (the nav entry is hidden otherwise — see App.tsx); this screen still degrades to a plain
// "not available" message if opened directly.
//
// OAuth: "Connect" sends the browser to Otter; Otter redirects back to /import/otter/callback
// with ?code&state, which this screen forwards to the API on mount (authenticated as the
// same user) and then strips from the URL.
import { useEffect, useRef, useState } from 'react';
import type { OtterImportRun } from '@scribe/core/types';
import { Badge, formatDate } from '../components/ui';
import {
  isActiveRun,
  takeOtterCallbackParams,
  useCompleteOtterConnect,
  useConnectOtter,
  useDisconnectOtter,
  useOtterImportRun,
  useOtterImportStatus,
  useStartOtterImport,
} from '../lib/otterImport';

const RUN_LABEL: Record<OtterImportRun['status'], { text: string; kind: string }> = {
  queued: { text: 'Queued', kind: 'pending' },
  running: { text: 'Importing', kind: 'info' },
  completed: { text: 'Complete', kind: 'success' },
  failed: { text: 'Failed', kind: 'danger' },
  cancelled: { text: 'Cancelled', kind: 'neutral' },
};

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.';
}

export function OtterImportScreen({ onOpenRecordings }: { onOpenRecordings?: () => void }) {
  const status = useOtterImportStatus();
  const connect = useConnectOtter();
  const complete = useCompleteOtterConnect();
  const start = useStartOtterImport();
  const disconnect = useDisconnectOtter();
  const [callbackError, setCallbackError] = useState<string | null>(null);
  const handledCallback = useRef(false);

  const latestRunId = start.data?.id ?? status.data?.latest_run?.id;
  const run = useOtterImportRun(latestRunId);
  const currentRun = run.data ?? status.data?.latest_run ?? null;

  // "Done" notice: only for a run this page watched finish, not one that finished earlier.
  const watchedActive = useRef<string | null>(null);
  const [finished, setFinished] = useState<OtterImportRun | null>(null);
  useEffect(() => {
    if (!currentRun) return;
    if (isActiveRun(currentRun)) {
      watchedActive.current = currentRun.id;
    } else if (watchedActive.current === currentRun.id && currentRun.status === 'completed') {
      watchedActive.current = null;
      setFinished(currentRun);
    }
  }, [currentRun]);

  useEffect(() => {
    if (handledCallback.current) return;
    handledCallback.current = true;
    const params = takeOtterCallbackParams();
    if (!params) return;
    if ('error' in params) {
      setCallbackError('Otter authorization was cancelled or failed. Try connecting again.');
      return;
    }
    complete.mutate(params, {
      onError: (err) => setCallbackError(errorMessage(err)),
    });
    // `complete` is stable for this purpose; run once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onConnect() {
    setCallbackError(null);
    const { authorize_url } = await connect.mutateAsync();
    window.location.assign(authorize_url);
  }

  if (status.isLoading) {
    return (
      <div className="card">
        <p className="muted">Loading…</p>
      </div>
    );
  }
  if (status.data === null || status.data === undefined) {
    return (
      <div className="card">
        <h1>Import from Otter</h1>
        <p className="muted">
          {status.isError
            ? 'Could not load Otter import status.'
            : 'Otter import is not available.'}
        </p>
      </div>
    );
  }

  const { connection, connected_at } = status.data;
  const connected = connection === 'connected';
  const active = isActiveRun(currentRun);
  const busy = connect.isPending || complete.isPending;

  return (
    <div className="card">
      <div className="card-head">
        <h1>Import from Otter</h1>
      </div>
      <p className="muted">
        Bring your past Otter meetings into Scribe so you can stop using Otter. Connect once and
        Scribe imports your whole Otter history. Each meeting becomes a Scribe recording with its
        Otter transcript and speaker names. Otter does not share audio, so imported meetings have
        no playback. Running the import again only adds meetings that are not already here.
      </p>

      {finished && (
        <div className="banner banner-success" role="status">
          <p>
            Done: {finished.imported} meeting{finished.imported === 1 ? '' : 's'} imported from Otter
            {finished.skipped > 0 && <> ({finished.skipped} already in Scribe)</>}.
          </p>
          {onOpenRecordings && finished.imported > 0 && (
            <button className="sm" onClick={onOpenRecordings}>
              View recordings
            </button>
          )}
        </div>
      )}

      {callbackError && (
        <div className="banner banner-danger" role="alert">
          <p>{callbackError}</p>
          <button className="sm" onClick={() => setCallbackError(null)}>
            Dismiss
          </button>
        </div>
      )}
      {connect.isError && (
        <p className="error" role="alert">
          {errorMessage(connect.error)}
        </p>
      )}

      <section aria-labelledby="otter-connection-heading" className="field">
        <h2 id="otter-connection-heading">Otter account</h2>
        {complete.isPending && <p className="muted">Finishing connection…</p>}
        {connected ? (
          <>
            <p>
              <Badge kind="success">Connected</Badge>
              {connected_at && <span className="muted"> since {formatDate(connected_at)}</span>}
            </p>
            <div className="row">
              <button
                className="ghost"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
              >
                Disconnect
              </button>
            </div>
          </>
        ) : (
          <>
            {connection === 'error' && (
              <p className="error">
                Otter stopped accepting Scribe&apos;s access. Reconnect to continue.
              </p>
            )}
            <div className="row">
              <button onClick={() => void onConnect()} disabled={busy}>
                {connection === 'error' ? 'Reconnect Otter' : 'Connect Otter and import'}
              </button>
            </div>
          </>
        )}
      </section>

      {connected && (
        <section aria-labelledby="otter-import-heading" className="field">
          <h2 id="otter-import-heading">History import</h2>
          <div className="row">
            <button onClick={() => start.mutate()} disabled={active || start.isPending}>
              {currentRun && !active ? 'Import again' : 'Start import'}
            </button>
            {currentRun && !active && currentRun.imported > 0 && onOpenRecordings && (
              <button className="ghost" onClick={onOpenRecordings}>
                View recordings
              </button>
            )}
          </div>
          {start.isError && (
            <p className="error" role="alert">
              {errorMessage(start.error)}
            </p>
          )}
          {currentRun && <RunProgress run={currentRun} />}
        </section>
      )}
    </div>
  );
}

function RunProgress({ run }: { run: OtterImportRun }) {
  const label = RUN_LABEL[run.status];
  const processed = run.imported + run.skipped + run.failed;
  return (
    <div aria-live="polite" data-testid="otter-run-progress">
      <p>
        <Badge kind={label.kind}>{label.text}</Badge>
        {run.finished_at && <span className="muted"> {formatDate(run.finished_at)}</span>}
      </p>
      <p>
        Found {run.discovered} · Imported {run.imported} · Already in Scribe {run.skipped}
        {run.failed > 0 && <> · Could not import {run.failed}</>}
      </p>
      {isActiveRun(run) && run.discovered > 0 && (
        <p className="muted small">
          {processed} of {run.discovered} found so far processed.
        </p>
      )}
      {isActiveRun(run) && (
        <p className="muted small">
          You can leave this page. The import keeps going, and this page shows how far it got
          when you come back.
        </p>
      )}
      {run.status === 'failed' && run.error_code === 'otter_auth' && (
        <p className="error">
          Otter rejected Scribe&apos;s access. Reconnect, then import again to continue.
        </p>
      )}
      {run.status === 'failed' && run.error_code !== 'otter_auth' && (
        <p className="error">
          The import stopped early. Import again to continue — meetings already imported are
          skipped.
        </p>
      )}
      {run.truncated_windows > 0 && (
        <p className="muted small">
          Some days had more meetings than Otter lists at once, so a few may be missing.
        </p>
      )}
    </div>
  );
}
